/**
 * ScopeChangeRefresher — one headless watcher that guarantees the page
 * always matches the current (organization, client) selection.
 *
 * Most data already re-fetches on its own: every agent-scoped query keys
 * on the selected agent id, and every org-scoped query keys on the
 * effective org id, so changing either one re-keys the query and React
 * Query refetches. This component is the belt-and-braces on top of that.
 * It watches BOTH ids and, the moment either changes, tells React Query
 * to refetch everything. That:
 *
 *   • closes the brief window after an org switch where a page could still
 *     be showing the previous org's data (the old gap — the refresh used
 *     to wait for the new org's agent list to finish loading), and
 *   • makes the "selection changed → page refreshes" behaviour hold for
 *     every page, without each page having to remember to key on the right
 *     id.
 *
 * It does NOT clear the selected client when the org changes. AutoSelectClient
 * already swaps the client to the new org's first agent once that org's
 * agent list loads; clearing it here would briefly bounce agent-scoped
 * pages (e.g. FIM) to their "pick a client" screen, which we don't want.
 *
 * Mounted once, in AppShell, next to AutoSelectOrg / AutoSelectClient.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffectiveOrgId } from "./hooks/useOrgPicker";
import { useSelectedClient } from "./hooks/useRegistry";

export const ScopeChangeRefresher: React.FC = () => {
  const orgId = useEffectiveOrgId();
  const { selected } = useSelectedClient();
  const agentId = selected?.agent_id ?? "";
  const qc = useQueryClient();

  // Remember what we last saw. `undefined` means "first render — nothing
  // to compare against yet", so we never fire a refresh on initial mount.
  const prevOrg = useRef<string | undefined>(undefined);
  const prevAgent = useRef<string | undefined>(undefined);

  useEffect(() => {
    const first = prevOrg.current === undefined && prevAgent.current === undefined;
    const orgChanged = !first && prevOrg.current !== orgId;
    const agentChanged = !first && prevAgent.current !== agentId;

    prevOrg.current = orgId;
    prevAgent.current = agentId;

    // On the very first render the page is mounting and will fetch on its
    // own — nothing to refresh.
    if (first) return;

    // Either selection changed → refetch every active query so the whole
    // page reflects the new org / client. This is user-driven and
    // infrequent, so the broad invalidation is cheap in practice (the same
    // approach AutoSelectClient already uses on an org-triggered swap).
    if (orgChanged || agentChanged) {
      void qc.invalidateQueries();
    }
  }, [orgId, agentId, qc]);

  return null;
};